import {
  Box,
  Button,
  Card,
  FormControl,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import useRetractOrderSLO from '../../../../../../hooks/orders/useRetractOrderSLO';
import { hasValidChars } from '../utils';
import CustomerInfos from './CustomerInfos';
import PickUpAddress from './PickUpAddress.jsx';
import PickUpDateTime from './PickUpDateTime.jsx';
import PickUpInfos from './PickUpInfos.jsx';
import RetractionConfirmModal from './RetractionConfirmModal.jsx';
import { setSnackBar } from '../../../../../../states/app/staticSlice.js';
import { useDispatch } from 'react-redux';

const RetractionFormSLO = ({
  orderRef,
  handleShowComponent,
  orderDetails,
  userId,
  retraction,
}) => {
  const dispatch = useDispatch();
  const { retractOrderSLO } = useRetractOrderSLO();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [formData, setFormData] = useState({
    ticketReference: orderDetails?.ticket?.reference,
    orderReference: orderDetails?.reference,
    comment: '',
    lastName: '',
    firstName: '',
    phone: '',
    pickUpDate: null,
    pickUpTime: '',
    address1: '',
    address2: '',
    postalCode: '',
    city: '',
    country: '',
    additionalInfos: '',
    trackingNumber: '',
    carrier: '',
    isSet: false,
  });
  const [formError, setFormError] = useState({
    comment: null,
    lastName: null,
    firstName: null,
    phone: null,
    pickUpDate: null,
    pickUpTime: null,
    address1: null,
    address2: null,
    postalCode: null,
    city: null,
    country: null,
    additionalInfos: null,
    trackingNumber: null,
    carrier: null,
  });

  const handleFormChange = ({ key, value }) => {
    if (key === 'multipleFields') {
      setFormData((prevData) => ({
        ...prevData,
        ...value,
      }));
    } else {
      if ((key === 'address1' || key === 'address2') && value?.length > 35) {
        setFormError({
          ...formError,
          [key]: "L'adresse ne doit pas dépasser 35 caractères",
        });
        return;
      }
      setFormData((prevData) => ({
        ...prevData,
        [key]: value,
      }));
    }
  };

  const validateFields = () => {
    let isValid = true;
    const newErrors = {};

    if (!formData?.lastName || formData.lastName?.length < 3) {
      newErrors.lastName = 'Le nom doit contenir au moins 3 caractères';
      isValid = false;
    }

    if (!formData?.firstName || formData.firstName?.length < 3) {
      newErrors.firstName = 'Le prénom doit contenir au moins 3 caractères';
      isValid = false;
    }

    if (!formData?.phone || formData.phone?.length < 10) {
      newErrors.phone =
        'Le numéro de téléphone doit contenir au moins 10 caractères';
      isValid = false;
    }

    if (!formData?.pickUpDate) {
      newErrors.pickUpDate = "Veuillez sélectionner une date d'enlèvement";
      isValid = false;
    }

    if (!formData?.pickUpTime) {
      newErrors.pickUpTime = "Veuillez sélectionner une heure d'enlèvement";
      isValid = false;
    }

    if (!formData?.address1) {
      newErrors.address1 = "L'adresse est requise";
      isValid = false;
    } else if (formData.address1?.length < 3) {
      newErrors.address1 = "L'adresse doit contenir au moins 3 caractères";
      isValid = false;
    }

    if (!formData?.postalCode) {
      newErrors.postalCode = 'Le code postal est requis';
      isValid = false;
    } else if (formData.postalCode?.length < 3) {
      newErrors.postalCode =
        'Le code postal doit contenir au moins 3 caractères';
      isValid = false;
    }

    if (!formData?.city) {
      newErrors.city = 'La ville est requise';
      isValid = false;
    } else if (formData.city?.length < 3) {
      newErrors.city = 'La ville doit contenir au moins 3 caractères';
      isValid = false;
    }

    if (!formData?.comment || formData.comment?.length < 10) {
      newErrors.comment = 'Le commentaire doit faire au moins 10 caractères';
      isValid = false;
    }

    // Validate tracking number and carrier dependency
    if (formData?.trackingNumber && !formData?.carrier) {
      newErrors.carrier =
        "Un transporteur est requis lorsqu'un numéro de suivi est renseigné";
      isValid = false;
    }

    if (formData?.carrier && !formData?.trackingNumber) {
      newErrors.trackingNumber =
        "Un numéro de suivi est requis lorsqu'un transporteur est sélectionné";
      isValid = false;
    }

    // Validate special characters in address fields
    const addressFields = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      address1: formData.address1,
      address2: formData.address2,
      postalCode: formData.postalCode,
      city: formData.city,
    };

    for (const [key, value] of Object.entries(addressFields)) {
      if (value && !hasValidChars(value)) {
        newErrors[key] = 'Le champ contient des caractères non valides';
        isValid = false;
      }
    }

    setFormError(newErrors);
    return isValid;
  };

  const handleOpenConfirm = () => {
    if (validateFields()) {
      setIsConfirmOpen(true);
    }
  };

  const onCloseModalConfirm = () => {
    setIsConfirmOpen(false);
    setConfirmLoading(false);
  };

  const handleConfirm = async () => {
    console.log('handleConfirm formData', formData);
    setConfirmLoading(true);
    try {
      console.log('handleConfirm formData', formData);
      const orderRetractResponse = await retractOrderSLO(orderRef, {
        ...formData,
        orderRetractionId: retraction?._id,
        userId,
      });

      if (!orderRetractResponse) {
        setConfirmLoading(false);
        throw new Error('Failed to retract order');
      }

      const messageError = `La commande ${orderRef} est en cours de rétractation.`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'success' })
      );
      setIsConfirmOpen(false);
      setConfirmLoading(false);
      handleShowComponent(null);
    } catch (err) {
      console.error('Failed to send retraction request:', err);
      const messageError = `Une erreur est survenue lors de la demande de rétractation de la commande ${orderRef}.`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'error' })
      );
      setConfirmLoading(false);
      setIsConfirmOpen(false);
    }
  };

  useEffect(() => {
    if (orderDetails?.addresses?.billing && !formData.isSet) {
      setFormData({
        ...formData,
        lastName: orderDetails?.addresses?.billing?.lastName,
        firstName: orderDetails?.addresses?.billing?.firstName,
        phone: orderDetails?.addresses?.billing?.phone,
        address1: orderDetails?.addresses?.billing?.line1,
        address2: orderDetails?.addresses?.billing?.line2,
        postalCode: orderDetails?.addresses?.billing?.postalCode,
        city: orderDetails?.addresses?.billing?.city,
        country: orderDetails?.addresses?.billing?.country,
        isSet: true,
      });
    }
  }, [orderDetails]);

  return (
    <>
      <Card
        elevation={5}
        sx={{
          width: '100%',
          padding: 2,
          gap: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CustomerInfos
          formData={formData}
          handleFormChange={handleFormChange}
          formError={formError}
        />

        <PickUpAddress
          formData={formData}
          orderDetails={orderDetails}
          handleFormChange={handleFormChange}
          formError={formError}
        />

        <PickUpDateTime
          formData={formData}
          handleFormChange={handleFormChange}
          formError={formError}
        />
        <Typography
          color="primary"
          sx={{ fontSize: '1em', fontWeight: 'bold', mb: 1 }}
        >
          Informations Transport
        </Typography>

        <Box sx={{ m: 0 }}>
          <Typography sx={{ fontSize: 14 }}>
            <strong>Nombre de colis:</strong> {retraction?.packages?.length}
          </Typography>

          {retraction?.packages?.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                p: 0,
                m: 0,
              }}
            >
              {retraction?.packages?.map((p, index) => (
                <Typography key={index} sx={{ fontSize: 12 }}>
                  {p.reference} - {p.rawWeight} kg
                </Typography>
              ))}
            </Box>
          )}
        </Box>

        <PickUpInfos
          formData={formData}
          handleFormChange={handleFormChange}
          formError={formError}
        />

        <FormControl
          error={Boolean(formError.comment)}
          fullWidth
          sx={{ mt: 2 }}
        >
          <Typography sx={{ fontSize: '0.8em' }}>Commentaire</Typography>
          <TextField
            multiline
            rows={4}
            value={formData.comment}
            onChange={(e) =>
              handleFormChange({ key: 'comment', value: e.target.value })
            }
            placeholder="Ajoutez des informations sur la rétractation."
            error={Boolean(formError.comment)}
            helperText={
              formError.comment || 'Texte à destination des opérateurs internes'
            }
          />
        </FormControl>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            variant="outlined"
            color="info"
            onClick={() => handleShowComponent(null)}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenConfirm}
          >
            Confirmer
          </Button>
        </Box>
      </Card>
      <RetractionConfirmModal
        confirmLoading={confirmLoading}
        isOpen={isConfirmOpen}
        onConfirm={handleConfirm}
        onClose={onCloseModalConfirm}
        formData={formData}
        retraction={retraction}
      />
    </>
  );
};

export default RetractionFormSLO;
